/**
 * STANDARD TOWER FORMAT.
 *
 * A tower is authored as a declarative `TowerSpec` and compiled by
 * `compileTower()` into the low-level physics `Structure` (nodes + members) that
 * the solver simulates and the renderer draws. This is the contract every tower
 * type conforms to — see src/game/designs/*.ts for the per-type designs.
 *
 * The model:
 *   - A `stack` of stacked panels forms the structural skeleton. Every tower —
 *     even a monopole — has a stack, because a pin-jointed truss needs width to
 *     resist bending (a single node-line has no lateral stiffness). Faces = 3
 *     (triangular mast) or 4 (square tower). The chord couple carries bending.
 *   - Optional `guys`: rings of tension-only cables to ground anchors.
 *   - Optional `shell`: tells the renderer to clad the stack in a tapered steel
 *     tube (a monopole *looks* like a pole even though it's a slim lattice
 *     inside). Purely a rendering concern; structure comes from the stack.
 *
 * All geometry is SI (metres). The summit of the hill is the origin; tower base
 * sits at y = 0 and the stack rises in +y. A `ground(x,z)` height function lets
 * guy anchors and the foundation sit on the real terrain rather than float.
 */
import {
  type Structure,
  type Section,
  type SectionKey,
  type Material,
  type MaterialKey,
  SECTIONS,
  MATERIALS,
  addMember,
  makeNode,
  computeMasses,
} from "@/physics/model";
import {
  type SoilType,
  type FoundationSpec,
  DEFAULT_SOIL,
  DEFAULT_FOUNDATION,
  SOIL_PRESETS,
  foundationCapacity,
} from "@/physics/foundation";
import {
  type BaseConnectionSpec,
  type ConnectionCapacity,
  RIGID_CONNECTION,
  connectionCapacity,
} from "@/physics/baseConnection";
import { type MountedLoad, deadWeight } from "@/game/appurtenances";

export type TowerType = "lattice" | "guyed" | "monopole";

/**
 * Face bracing pattern for a lattice panel:
 * - "X" — cross bracing: both diagonals (default, stiffest).
 * - "K" — K-bracing: the horizontal girt is split at mid-span and two diagonals
 *   rise from there to the leg joints above (a Λ per panel → a K across stacked
 *   panels). Shorter diagonals than X — common on transmission/self-support towers.
 * - "diagonal" — a single diagonal per panel, alternating direction each level
 *   (zigzag / single-diagonal bracing — lighter, for lower-load runs).
 * - "none" — legs + horizontal girts only (unbraced face).
 */
export type BracingPattern = "X" | "K" | "diagonal" | "none";

/**
 * A section or material can be given either by catalog key (e.g. "legHeavy",
 * "a572") or inline as a full object — so a design can dial in thicker steel or
 * a different grade beyond the presets. See resolveSection/resolveMaterial.
 */
export type SectionRef = SectionKey | Section;
export type MaterialRef = MaterialKey | Material;

export function resolveSection(ref: SectionRef): Section {
  return typeof ref === "string" ? SECTIONS[ref] : ref;
}
export function resolveMaterial(ref: MaterialRef): Material {
  return typeof ref === "string" ? MATERIALS[ref] : ref;
}

/** The stacked-panel skeleton — the structural backbone of every tower. */
export interface PanelStack {
  faces: number; // legs per level: 3 (triangular) or 4 (square)
  panels: number; // number of stacked braced panels
  baseRadius: number; // circumradius at the base (m)
  topRadius: number; // circumradius at the top (m)
  legSection: SectionRef;
  braceSection: SectionRef;
  legMaterial?: MaterialRef; // steel grade for legs (default a572)
  braceMaterial?: MaterialRef; // steel grade for bracing (default a572)
  legType?: string; // optional reference into TowerSpec.legTypes
  braceType?: string; // optional reference into TowerSpec.braceTypes
  bracing?: BracingPattern; // face bracing pattern
  /**
   * Optional per-level circumradius profile (length `panels + 1`, bottom → top).
   * When given it OVERRIDES the linear baseRadius→topRadius taper, so a tower can
   * splay, pinch to a waist, or flare (the Eiffel Tower's curved legs, Sutro
   * Tower's wide tripod tapering to a slim mast). baseRadius/topRadius are still
   * used as validation ranges. Every value must be > 0.
   */
  radii?: number[];
}

/**
 * A horizontal cantilever platform ("crossarm") sticking out from the mast at a
 * height — Sutro Tower's antenna candelabra, the Eiffel Tower's observation
 * decks. One outer node per face is added at `radius`, tied to the ring with a
 * horizontal arm plus up/down diagonal stays and a tangential ring tie, so every
 * platform is fully triangulated (not a mechanism). Renders as a visible deck and
 * gives non-prismatic towers their recognizable silhouette. `crossarms` sit on
 * `TowerSpec` (they attach to whichever stack/segments built the rings).
 */
export interface CrossArm {
  atHeightFrac: number; // 0..1 up the tower where the platform sits
  radius: number; // horizontal reach from the axis (m) — the platform half-width
  section?: SectionRef; // arm/stay member (default: the band's braceSection at that level)
  material?: MaterialRef; // steel grade (default: that band's braceMaterial)
}

/**
 * A slim braced VERTICAL crown rising above the mast — Sutro Tower's three-pronged
 * antenna "trident". Builds a short `count`-sided X-braced mini-mast from the ring
 * at `atHeightFrac` up by `rise`, tapering `spread` → `topSpread`. Renders as the
 * antenna cluster that crowns tall broadcast towers.
 */
export interface AntennaMast {
  atHeightFrac: number; // 0..1 — where the prongs' feet sit on the tower
  count: number; // number of prongs (3 = trident)
  spread: number; // circumradius of the prong cluster at its base (m)
  topSpread?: number; // circumradius at the top (default: spread)
  rise: number; // vertical height the prongs extend above their feet (m)
  panels?: number; // sub-panels for bracing (default 4)
  section?: SectionRef; // prong member (default: the band's legSection there)
  material?: MaterialRef;
}

/**
 * Ornamental base ARCHES spanning between adjacent legs — the Eiffel Tower's four
 * great arches under the first platform (and Tokyo Tower's). For each face a
 * parabolic polyline of members springs from the two legs at `springFrac` and
 * rises to a crown at `apexFrac` (mid-face), each node tied up to the ring at the
 * apex level so the arch is braced (not a floppy chain). Renders as the arch.
 */
export interface BaseArch {
  springFrac: number; // 0..1 height where the arch feet spring from the legs
  apexFrac: number; // 0..1 height the arch crown reaches (e.g. the first platform)
  segments?: number; // polyline resolution (default 8)
  section?: SectionRef;
  material?: MaterialRef;
}

/**
 * One section of a MULTI-SEGMENT tower — a height band with its own geometry,
 * sections and bracing. Real towers stack these (heavy braced base → light top,
 * a pole-top mast). The base of each section is the previous section's top (the
 * first section's base is `SegmentedStack.baseRadius`).
 */
export interface StackSection {
  toHeight: number; // absolute top height of this section (m); ascending; last === spec.height
  topRadius: number; // circumradius at the top of this section (m)
  panels: number;
  legSection: SectionRef;
  braceSection: SectionRef;
  legMaterial?: MaterialRef;
  braceMaterial?: MaterialRef;
  legType?: string;
  braceType?: string;
  bracing?: BracingPattern;
}

/** A stack that changes width/section/bracing with height (alternative to `stack`). */
export interface SegmentedStack {
  faces: number; // 3 or 4, constant up the whole tower
  baseRadius: number; // circumradius at y = 0
  sections: StackSection[]; // stacked bottom → top
}

/** A ring of guys attaching at a height and running to ground anchors. */
export interface GuyRing {
  atHeightFrac: number; // 0..1 up the tower where the guys attach
  anchorRadius: number; // horizontal distance from base to the anchors (m)
  section: SectionRef;
  material?: MaterialRef; // default EHS guy strand
  guyType?: string; // optional reference into TowerSpec.guyTypes
  pretensionStrain: number; // rest-length shortening → installed tension
}

export interface MemberTypeSpec {
  id: string;
  name?: string;
  section: SectionRef;
  material?: MaterialRef;
}

/** Renderer hint: clad the stack in a tapered tube (monopole look). */
export interface TubeShell {
  baseDiameter: number;
  topDiameter: number;
}

/**
 * Below-ground info stored on the tower: ground (soil), concrete footing(s), and
 * the steel-to-concrete anchoring. All optional — omitted parts fall back to
 * competent defaults so existing towers are unaffected. Capacities/failure modes
 * (bearing, overturning, uplift, anchor breakout) live in physics/foundation.ts
 * and physics/baseConnection.ts; wiring them into the solver is the next step.
 */
export interface TowerFoundation {
  soil?: SoilType; // ground conditions (bearing, friction, cohesion)
  base?: FoundationSpec; // main footing/pier (includes concrete grade)
  anchor?: FoundationSpec; // guy-anchor footing (guyed masts)
  connection?: BaseConnectionSpec; // steel↔concrete: anchor bolts / stub / rebar
}

/** Foundation with defaults resolved, carried on a compiled tower. */
export interface ResolvedFoundation {
  soil: SoilType;
  base: FoundationSpec;
  anchor?: FoundationSpec;
  connection?: BaseConnectionSpec;
}

/**
 * A tower built DIRECTLY from a parsed 3D model's geometry — the mesh EDGES are
 * the actual members, in their real positions, instead of a synthesized
 * parametric lattice. Used for landmark towers whose exact silhouette comes from
 * a real STL/DWG/DXF (e.g. the Eiffel Tower). `nodes` are [x,y,z] in metres
 * (y-up, base at y=0); `edges` index into `nodes`. Nodes at/below `baseFixedBelow`
 * (m) are pinned to the foundation. When `mesh` is set, stack/segments/crossarms/
 * arches are all ignored — the geometry IS the model.
 */
export interface MeshSource {
  nodes: [number, number, number][];
  edges: [number, number][];
  section: SectionRef; // member section applied to every edge
  material?: MaterialRef;
  baseFixedBelow?: number; // y (m) under which nodes are pinned (default: lowest 3% of height)
}

export interface TowerSpec {
  type: TowerType;
  height: number;
  legTypes?: MemberTypeSpec[];
  braceTypes?: MemberTypeSpec[];
  guyTypes?: MemberTypeSpec[];
  mesh?: MeshSource; // build directly from real parsed geometry (overrides stack/segments)
  stack?: PanelStack; // uniform single-segment skeleton
  segments?: SegmentedStack; // OR a multi-segment skeleton (exactly one of stack/segments)
  crossarms?: CrossArm[]; // optional horizontal platforms (candelabra / observation decks)
  masts?: AntennaMast[]; // optional vertical antenna crowns (Sutro Tower's trident)
  arches?: BaseArch[]; // optional ornamental base arches (Eiffel/Tokyo Tower)
  guys?: GuyRing[];
  shell?: TubeShell;
  foundation?: TowerFoundation; // ground / concrete / anchoring (optional)
  loads?: MountedLoad[]; // appurtenances hung on the tower (optional, default none)
}

/** How to draw a compiled tower — indices into structure.nodes. */
export interface RenderModel {
  skin: "lattice" | "tube";
  rings: number[][]; // node indices per level, bottom → top (the centerline)
  tubeRadius?: number[]; // outer radius per level if skin === 'tube'
  baseNodes: number[]; // fixed foundation nodes
  anchors: number[]; // fixed guy-anchor nodes
}

export interface BuiltTower {
  structure: Structure;
  render: RenderModel;
  type: TowerType;
  height: number;
  halfWidth: number; // largest footprint radius, for camera framing
  foundation: ResolvedFoundation; // ground / concrete / anchoring (defaults resolved)
}

export type GroundFn = (x: number, z: number) => number;

export interface TowerMetrics {
  height: number; // m
  steelMassKg: number; // total member steel mass (A·L·ρ summed)
  sailAreaM2: number; // total member drag area (Σ dragWidth·L), a wind-sail proxy
  memberCount: number;
  baseWidthM: number; // base footprint width
}

/** Physical summary of a built tower — for the readout / future scoring. */
export function towerMetrics(t: BuiltTower): TowerMetrics {
  let mass = 0;
  let sail = 0;
  for (const m of t.structure.members) {
    mass += m.section.A * m.L0 * m.material.rho;
    sail += m.section.dragWidth * m.L0;
  }
  let baseR = 0;
  for (const idx of t.render.baseNodes) {
    const n = t.structure.nodes[idx]!;
    baseR = Math.max(baseR, Math.hypot(n.x, n.z));
  }
  return {
    height: t.height,
    steelMassKg: mass,
    sailAreaM2: sail,
    memberCount: t.structure.members.length,
    baseWidthM: baseR * 2,
  };
}

/**
 * A shippable tower design: metadata + a spec. Every file in src/game/designs/
 * exports one of these as `design`, and the registry auto-discovers them. This
 * is the unit end users and agents author. See docs/TOWER_SPEC.md.
 */
/**
 * The authored 2D free-draw graph (front-elevation X–Y). This is the SOURCE of a
 * hand-drawn tower; the 3D `structure` is DERIVED from it by extrusion. Persisting
 * the graph (not the derived 3D structure) lets a drawn tower round-trip exactly
 * through save / export / share and stay editable — reconstructing 2D from the
 * wrapped 3D structure is lossy (axis/apex nodes collapse onto z = 0 and drop out).
 */
export interface FreeGraph {
  nodes: Array<{ id: number; x: number; y: number }>;
  members: Array<{ id: number; a: number; b: number; section: SectionKey; material?: MaterialKey }>;
  idc: number; // next node/member id counter
}

export interface TowerDesign {
  id: string; // unique, kebab-case (e.g. "rohn-25g-guyed")
  name: string; // display name shown in the UI
  description: string; // one-line summary
  author?: string; // optional attribution
  spec: TowerSpec;
  freeGraph?: FreeGraph; // authored 2D graph for a hand-drawn tower (the source of truth)
  structure?: Structure; // legacy: derived 3D freeform build state (pre-freeGraph files)
}

export interface ValidationResult {
  ok: boolean;
  errors: string[];
  warnings: string[];
}

const SECTION_KEYS = Object.keys(SECTIONS) as SectionKey[];
const MATERIAL_KEYS = Object.keys(MATERIALS) as MaterialKey[];

function badSection(ref: SectionRef | undefined): string | null {
  if (ref === undefined) return "missing";
  if (typeof ref === "string")
    return SECTION_KEYS.includes(ref as SectionKey) ? null : `unknown section "${ref}"`;
  if (!(ref.A > 0) || !(ref.I > 0) || !(ref.dragWidth > 0))
    return "inline section needs A>0, I>0, dragWidth>0";
  return null;
}
function badMaterial(ref: MaterialRef | undefined): string | null {
  if (ref === undefined) return null; // optional → default
  if (typeof ref === "string")
    return MATERIAL_KEYS.includes(ref as MaterialKey) ? null : `unknown material "${ref}"`;
  if (!(ref.E > 0) || !(ref.Fy > 0) || !(ref.rho > 0))
    return "inline material needs E>0, Fy>0, rho>0";
  return null;
}

/**
 * Validate a design: hard `errors` block it from loading; `warnings` flag
 * dubious-but-loadable choices (e.g. an out-of-range base/height ratio). Keep
 * this the single source of truth for what a valid tower is.
 */
export function validateTower(d: TowerDesign): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const err = (m: string) => errors.push(m);
  const warn = (m: string) => warnings.push(m);

  if (!d.id || !/^[a-z0-9-]+$/.test(d.id)) err(`id must be kebab-case, got "${d.id}"`);
  if (!d.name) err("name is required");
  const spec = d.spec;
  if (!spec) return { ok: false, errors: ["spec is missing"], warnings };

  if (!(spec.height > 0)) err("height must be > 0");
  const validateMemberTypes = (label: "legTypes" | "braceTypes" | "guyTypes", types: MemberTypeSpec[] | undefined) => {
    const seen = new Set<string>();
    types?.forEach((t, i) => {
      if (!t.id || !/^[a-zA-Z0-9_-]+$/.test(t.id)) err(`${label}[${i}].id is required`);
      if (seen.has(t.id)) err(`${label}[${i}].id "${t.id}" is duplicated`);
      seen.add(t.id);
      const es = badSection(t.section);
      if (es) err(`${label}[${i}].section: ${es}`);
      const em = badMaterial(t.material);
      if (em) err(`${label}[${i}].material: ${em}`);
    });
  };
  validateMemberTypes("legTypes", spec.legTypes);
  validateMemberTypes("braceTypes", spec.braceTypes);
  validateMemberTypes("guyTypes", spec.guyTypes);
  const hasType = (types: MemberTypeSpec[] | undefined, id: string | undefined) =>
    id === undefined || !!types?.some((t) => t.id === id);
  const st = spec.stack;
  const seg = spec.segments;
  if (spec.mesh) {
    // mesh-built tower: geometry comes from parsed model edges, not stack/segments
    if (!(spec.mesh.nodes?.length > 0) || !(spec.mesh.edges?.length > 0))
      err("mesh needs non-empty nodes and edges");
    const ems = badSection(spec.mesh.section);
    if (ems) err(`mesh.section: ${ems}`);
    const emm = badMaterial(spec.mesh.material);
    if (emm) err(`mesh.material: ${emm}`);
  } else if (!st && !seg) {
    err("either stack, segments, or mesh is required");
  }
  if (st && seg) warn("both stack and segments given — segments takes precedence");
  if (st) {
    if (st.faces !== 3 && st.faces !== 4 && st.faces < 3) err("stack.faces must be 3 or 4 (or ≥3)");
    if (!(st.panels >= 1)) err("stack.panels must be ≥ 1");
    if (!(st.baseRadius > 0)) err("stack.baseRadius must be > 0");
    if (!(st.topRadius > 0)) err("stack.topRadius must be > 0");
    const eLeg = badSection(st.legSection);
    if (eLeg) err(`legSection: ${eLeg}`);
    const eBr = badSection(st.braceSection);
    if (eBr) err(`braceSection: ${eBr}`);
    const eLegM = badMaterial(st.legMaterial);
    if (eLegM) err(`legMaterial: ${eLegM}`);
    const eBrM = badMaterial(st.braceMaterial);
    if (eBrM) err(`braceMaterial: ${eBrM}`);
    if (!hasType(spec.legTypes, st.legType)) err(`stack.legType "${st.legType}" is not defined`);
    if (!hasType(spec.braceTypes, st.braceType)) err(`stack.braceType "${st.braceType}" is not defined`);

    // engineering sanity (warnings only)
    if (spec.height > 0 && st.baseRadius > 0) {
      const baseWidth = st.baseRadius * 2;
      const ratio = baseWidth / spec.height;
      const guyed = (spec.guys?.length ?? 0) > 0;
      // only self-supporting lattice towers are expected to hold this ratio;
      // guyed masts and monopoles are deliberately slender.
      if (spec.type === "lattice" && !guyed && (ratio < 1 / 14 || ratio > 1 / 4))
        warn(`base/height ratio ${ratio.toFixed(3)} is outside the usual 1/6–1/12 for self-supporting towers`);
    }
    const panelH = spec.height / Math.max(1, st.panels);
    if (panelH > 6) warn(`panel height ${panelH.toFixed(1)} m is large — long unbraced legs buckle easily`);

    if (st.radii) {
      const need = st.panels + 1;
      if (st.radii.length !== need)
        err(`stack.radii must have ${need} entries (panels + 1), got ${st.radii.length}`);
      if (st.radii.some((r) => !(r > 0))) err("every stack.radii value must be > 0");
    }
  }
  if (seg) {
    if (seg.faces !== 3 && seg.faces !== 4 && seg.faces < 3) err("segments.faces must be 3 or 4 (or ≥3)");
    if (!(seg.baseRadius > 0)) err("segments.baseRadius must be > 0");
    if (!seg.sections || seg.sections.length === 0) err("segments.sections must be non-empty");
    else {
      let prevH = 0;
      seg.sections.forEach((sc, i) => {
        if (!(sc.toHeight > prevH)) err(`segments.sections[${i}].toHeight must ascend (> ${prevH})`);
        prevH = sc.toHeight;
        if (!(sc.topRadius > 0)) err(`segments.sections[${i}].topRadius must be > 0`);
        if (!(sc.panels >= 1)) err(`segments.sections[${i}].panels must be ≥ 1`);
        const es = badSection(sc.legSection);
        if (es) err(`segments.sections[${i}].legSection: ${es}`);
        const eb2 = badSection(sc.braceSection);
        if (eb2) err(`segments.sections[${i}].braceSection: ${eb2}`);
        const em = badMaterial(sc.legMaterial);
        if (em) err(`segments.sections[${i}].legMaterial: ${em}`);
        const emb = badMaterial(sc.braceMaterial);
        if (emb) err(`segments.sections[${i}].braceMaterial: ${emb}`);
        if (!hasType(spec.legTypes, sc.legType)) err(`segments.sections[${i}].legType "${sc.legType}" is not defined`);
        if (!hasType(spec.braceTypes, sc.braceType)) err(`segments.sections[${i}].braceType "${sc.braceType}" is not defined`);
      });
      const lastH = seg.sections[seg.sections.length - 1]!.toHeight;
      if (spec.height > 0 && Math.abs(lastH - spec.height) > 0.01)
        warn(`last section toHeight ${lastH} m ≠ spec.height ${spec.height} m`);
    }
  }

  if (spec.crossarms) {
    spec.crossarms.forEach((a, i) => {
      if (!(a.atHeightFrac > 0 && a.atHeightFrac < 1))
        err(`crossarms[${i}].atHeightFrac must be in (0,1)`);
      if (!(a.radius > 0)) err(`crossarms[${i}].radius must be > 0`);
      if (a.section !== undefined) {
        const ea = badSection(a.section);
        if (ea) err(`crossarms[${i}].section: ${ea}`);
      }
      const eam = badMaterial(a.material);
      if (eam) err(`crossarms[${i}].material: ${eam}`);
    });
  }

  if (spec.masts) {
    spec.masts.forEach((m, i) => {
      if (!(m.atHeightFrac > 0 && m.atHeightFrac <= 1)) err(`masts[${i}].atHeightFrac must be in (0,1]`);
      if (!(m.count >= 2)) err(`masts[${i}].count must be ≥ 2`);
      if (!(m.spread > 0)) err(`masts[${i}].spread must be > 0`);
      if (!(m.rise > 0)) err(`masts[${i}].rise must be > 0`);
      if (m.section !== undefined) {
        const em2 = badSection(m.section);
        if (em2) err(`masts[${i}].section: ${em2}`);
      }
      const emm = badMaterial(m.material);
      if (emm) err(`masts[${i}].material: ${emm}`);
    });
  }

  if (spec.arches) {
    spec.arches.forEach((a, i) => {
      if (!(a.springFrac >= 0 && a.springFrac < 1)) err(`arches[${i}].springFrac must be in [0,1)`);
      if (!(a.apexFrac > a.springFrac && a.apexFrac <= 1))
        err(`arches[${i}].apexFrac must be in (springFrac, 1]`);
      if (a.section !== undefined) {
        const ea2 = badSection(a.section);
        if (ea2) err(`arches[${i}].section: ${ea2}`);
      }
      const eam2 = badMaterial(a.material);
      if (eam2) err(`arches[${i}].material: ${eam2}`);
    });
  }

  if (spec.guys) {
    spec.guys.forEach((g, i) => {
      if (!(g.atHeightFrac > 0 && g.atHeightFrac <= 1)) err(`guys[${i}].atHeightFrac must be in (0,1]`);
      if (!(g.anchorRadius > 0)) err(`guys[${i}].anchorRadius must be > 0`);
      const eg = badSection(g.section);
      if (eg) err(`guys[${i}].section: ${eg}`);
      const egm = badMaterial(g.material);
      if (egm) err(`guys[${i}].material: ${egm}`);
      if (!hasType(spec.guyTypes, g.guyType)) err(`guys[${i}].guyType "${g.guyType}" is not defined`);
      if (g.pretensionStrain < 0 || g.pretensionStrain > 0.02)
        warn(`guys[${i}].pretensionStrain ${g.pretensionStrain} is unusual (typical 0.0005–0.003)`);
    });
    if (spec.height > 0 && spec.guys.length > 0) {
      const maxR = Math.max(...spec.guys.map((g) => g.anchorRadius));
      const r = maxR / spec.height;
      if (r < 0.4 || r > 1.2) warn(`guy anchor radius ${r.toFixed(2)}× height is outside the usual 0.6–1.0×`);
    }
  }

  if (spec.type === "monopole" && !spec.shell)
    warn("monopole has no shell — it will render as a bare lattice, not a tube");
  if (spec.type === "guyed" && !(spec.guys && spec.guys.length))
    warn("guyed tower has no guys — it will behave as a free-standing mast");

  return { ok: errors.length === 0, errors, warnings };
}

/**
 * Default guy-anchor footing when a design gives no `foundation.anchor`: a buried
 * deadman block, sized generously so guys hold on the default dense-sand soil
 * (matches docs/FOUNDATIONS.md §8). Only used for towers that have guy anchors.
 */
const DEFAULT_ANCHOR_FOUNDATION: FoundationSpec = {
  type: "deadman",
  width: 1.5, // 1.5 m square block
  diameter: 0.6, // unused for deadman
  depth: 2.0, // 2 m burial → deep soil cone for uplift resistance
  concrete: DEFAULT_FOUNDATION.concrete,
};

function ringPositions(faces: number, radius: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let c = 0; c < faces; c++) {
    const a = (c / faces) * Math.PI * 2 + Math.PI / faces;
    out.push([Math.cos(a) * radius, Math.sin(a) * radius]);
  }
  return out;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Resolve a spec's foundation to concrete capacities and attach `foundationCap`
 * to each fixed base node — the same wiring compileTower does, exposed so the
 * free-draw path can give a hand-drawn tower real, spec-driven foundations too
 * (soil / footing / pad / concrete), instead of a hard-coded default.
 */
export function resolveFoundationFor(
  s: Structure,
  baseNodes: number[],
  specFoundation: TowerSpec["foundation"],
): ResolvedFoundation {
  const foundation: ResolvedFoundation = {
    soil: specFoundation?.soil ?? DEFAULT_SOIL,
    base: specFoundation?.base ?? DEFAULT_FOUNDATION,
    anchor: specFoundation?.anchor,
    connection: specFoundation?.connection,
  };
  const soilParams = SOIL_PRESETS[foundation.soil];
  const connCap = foundation.connection ? connectionCapacity(foundation.connection) : RIGID_CONNECTION;
  const baseCap = foundationCapacity(foundation.base, soilParams);
  for (const idx of baseNodes) {
    const n = s.nodes[idx];
    if (n) n.foundationCap = { capacity: baseCap, connection: connCap, isAnchor: false };
  }
  return foundation;
}

/** Compile a declarative spec into a simulable + renderable tower. */
interface CompileBand {
  leg: Section;
  brace: Section;
  legMat: Material;
  braceMat: Material;
  bracing: BracingPattern;
}

/**
 * Build a tower straight from a parsed mesh: every edge becomes a member at its
 * real position. No parametric lattice — the geometry is exactly the source model.
 */
function compileMesh(spec: TowerSpec, s: Structure): BuiltTower {
  const mesh = spec.mesh!;
  const section = resolveSection(mesh.section);
  const material = resolveMaterial(mesh.material ?? "a572");
  const maxY = Math.max(...mesh.nodes.map((n) => n[1]));
  const fixBelow = mesh.baseFixedBelow ?? maxY * 0.03;
  let halfWidth = 0;
  for (const [x, y, z] of mesh.nodes) {
    s.nodes.push(makeNode(x, y, z, y <= fixBelow));
    halfWidth = Math.max(halfWidth, Math.hypot(x, z));
  }
  for (const [i, j] of mesh.edges) {
    if (i >= 0 && j >= 0 && i < s.nodes.length && j < s.nodes.length && i !== j)
      addMember(s, i, j, section, { material });
  }
  computeMasses(s);
  const baseNodes: number[] = [];
  s.nodes.forEach((n, i) => n.fixed && baseNodes.push(i));
  const render: RenderModel = { skin: "lattice", rings: [], baseNodes, anchors: [] };
  const foundation: ResolvedFoundation = {
    soil: spec.foundation?.soil ?? DEFAULT_SOIL,
    base: spec.foundation?.base ?? DEFAULT_FOUNDATION,
    anchor: spec.foundation?.anchor,
    connection: spec.foundation?.connection,
  };
  const soilParams = SOIL_PRESETS[foundation.soil];
  const connCap = foundation.connection ? connectionCapacity(foundation.connection) : RIGID_CONNECTION;
  const baseCap = foundationCapacity(foundation.base, soilParams);
  for (const idx of baseNodes)
    s.nodes[idx]!.foundationCap = { capacity: baseCap, connection: connCap, isAnchor: false };
  return { structure: s, render, type: spec.type, height: spec.height, halfWidth, foundation };
}

export function compileTower(spec: TowerSpec, ground: GroundFn = () => 0): BuiltTower {
  const s: Structure = { nodes: [], members: [] };
  if (spec.mesh) return compileMesh(spec, s);

  // Normalise either a uniform `stack` or a multi-`segments` skeleton into a flat
  // list of rings (y, radius) + a per-panel band (sections/materials/bracing), so
  // the rest of the compiler is identical for both. Segments chain: each section's
  // base is the previous section's top (the first section's base is baseRadius).
  const ringY: number[] = [];
  const ringR: number[] = [];
  const bands: CompileBand[] = [];
  let faces: number;
  const typed = (
    types: MemberTypeSpec[] | undefined,
    typeId: string | undefined,
    section: SectionRef,
    material: MaterialRef | undefined,
    defaultMaterial: MaterialRef,
  ): { section: Section; material: Material } => {
    const t = typeId ? types?.find((q) => q.id === typeId) : undefined;
    return {
      section: resolveSection(t?.section ?? section),
      material: resolveMaterial(t?.material ?? material ?? defaultMaterial),
    };
  };
  const bandOf = (
    legSection: SectionRef,
    braceSection: SectionRef,
    legMaterial: MaterialRef | undefined,
    braceMaterial: MaterialRef | undefined,
    legType: string | undefined,
    braceType: string | undefined,
    bracing: BracingPattern | undefined,
  ): CompileBand => ({
    leg: typed(spec.legTypes, legType, legSection, legMaterial, "a572").section,
    brace: typed(spec.braceTypes, braceType, braceSection, braceMaterial, "a572").section,
    legMat: typed(spec.legTypes, legType, legSection, legMaterial, "a572").material,
    braceMat: typed(spec.braceTypes, braceType, braceSection, braceMaterial, "a572").material,
    bracing: bracing ?? "X",
  });

  if (spec.segments) {
    faces = spec.segments.faces;
    ringY.push(0);
    ringR.push(spec.segments.baseRadius);
    let y0 = 0;
    let r0 = spec.segments.baseRadius;
    for (const sec of spec.segments.sections) {
      const band = bandOf(sec.legSection, sec.braceSection, sec.legMaterial, sec.braceMaterial, sec.legType, sec.braceType, sec.bracing);
      const np = Math.max(1, sec.panels);
      for (let k = 1; k <= np; k++) {
        const t = k / np;
        ringY.push(lerp(y0, sec.toHeight, t));
        ringR.push(lerp(r0, sec.topRadius, t));
        bands.push(band);
      }
      y0 = sec.toHeight;
      r0 = sec.topRadius;
    }
  } else {
    const st = spec.stack!;
    faces = st.faces;
    const band = bandOf(st.legSection, st.braceSection, st.legMaterial, st.braceMaterial, st.legType, st.braceType, st.bracing);
    const np = Math.max(1, st.panels);
    // an explicit per-level radii[] profile (length np+1) overrides the linear taper
    const useRadii = !!st.radii && st.radii.length === np + 1;
    ringY.push(0);
    ringR.push(useRadii ? st.radii![0]! : st.baseRadius);
    for (let k = 1; k <= np; k++) {
      const t = k / np;
      ringY.push(lerp(0, spec.height, t));
      ringR.push(useRadii ? st.radii![k]! : lerp(st.baseRadius, st.topRadius, t));
      bands.push(band);
    }
  }
  const panels = bands.length;
  const baseRadius = ringR[0]!;

  // nearest ring level to an absolute height (works for non-uniform panels)
  const levelAtHeight = (h: number): number => {
    let best = 0;
    let bestD = Infinity;
    for (let l = 0; l < ringY.length; l++) {
      const d = Math.abs(ringY[l]! - h);
      if (d < bestD) {
        bestD = d;
        best = l;
      }
    }
    return best;
  };

  // --- ring nodes ---
  const rings: number[][] = [];
  for (let lvl = 0; lvl < ringY.length; lvl++) {
    const y = ringY[lvl]!;
    const r = ringR[lvl]!;
    const row: number[] = [];
    for (const [x, z] of ringPositions(faces, r)) {
      const node = makeNode(x, y, z, lvl === 0);
      s.nodes.push(node);
      row.push(s.nodes.length - 1);
    }
    rings.push(row);
  }

  // --- members per panel using that panel's band ---
  for (let lvl = 0; lvl < panels; lvl++) {
    const band = bands[lvl]!;
    const brace = (i: number, j: number) => addMember(s, i, j, band.brace, { material: band.braceMat });
    const kApex: number[] = []; // K-brace apex nodes on this ring, for plan bracing
    for (let c = 0; c < faces; c++) {
      const next = (c + 1) % faces;
      const a = rings[lvl]![c]!;
      const aNext = rings[lvl]![next]!;
      const top = rings[lvl + 1]![c]!;
      const topNext = rings[lvl + 1]![next]!;
      addMember(s, a, top, band.leg, { material: band.legMat }); // leg
      if (band.bracing === "K") {
        // K-brace: split the horizontal girt at a mid node, two diagonals rise from
        // it to the leg joints above (a Λ per panel → a K across panels). The apex
        // is pulled inboard so the girt halves form a V, and all apexes on the ring
        // are plan-braced below — otherwise the apex is an out-of-plane mechanism.
        const na = s.nodes[a]!;
        const nb = s.nodes[aNext]!;
        const inset = 0.82; // apex radius as a fraction of the girt-midpoint radius
        s.nodes.push(makeNode(((na.x + nb.x) / 2) * inset, na.y, ((na.z + nb.z) / 2) * inset, false));
        const mid = s.nodes.length - 1;
        brace(a, mid);
        brace(mid, aNext); // split girt (shallow inward V)
        brace(mid, top);
        brace(mid, topNext); // K diagonals up to the leg joints
        kApex.push(mid);
      } else {
        brace(a, aNext); // horizontal girt
        if (band.bracing === "X") {
          brace(a, topNext);
          brace(aNext, top); // full cross
        } else if (band.bracing === "diagonal") {
          // single diagonal, flipping each level/face → a continuous zigzag
          if ((lvl + c) % 2 === 0) brace(a, topNext);
          else brace(aNext, top);
        }
        // "none": legs + girts only
      }
    }
    // plan bracing for K apexes: tie them across the section so they can't swing
    // out of their face planes (triangle for a 3-face mast, crossed for 4 faces).
    if (kApex.length === 4) {
      brace(kApex[0]!, kApex[2]!);
      brace(kApex[1]!, kApex[3]!);
    } else if (kApex.length >= 3) {
      for (let i = 0; i < kApex.length; i++) brace(kApex[i]!, kApex[(i + 1) % kApex.length]!);
    }
  }
  const topBand = bands[panels - 1]!;
  for (let c = 0; c < faces; c++) {
    addMember(s, rings[panels]![c]!, rings[panels]![(c + 1) % faces]!, topBand.brace, {
      material: topBand.braceMat,
    });
  }

  // --- crossarms: horizontal cantilever platforms (candelabra / decks) ---
  // Add an outer node per face at the platform radius and triangulate it back to
  // the mast: a horizontal arm to the ring node, up/down diagonal stays to the
  // rings above/below, and a tangential tie to the neighbouring platform node.
  // These render as visible decks (structureView draws every member).
  for (const arm of spec.crossarms ?? []) {
    const lvl = Math.min(panels - 1, Math.max(1, levelAtHeight(arm.atHeightFrac * spec.height)));
    const band = bands[Math.min(lvl, panels - 1)]!;
    const armSection = arm.section ? resolveSection(arm.section) : band.brace;
    const armMat = arm.material ? resolveMaterial(arm.material) : band.braceMat;
    const y = ringY[lvl]!;
    const outer: number[] = [];
    for (const [x, z] of ringPositions(faces, arm.radius)) {
      s.nodes.push(makeNode(x, y, z, false));
      outer.push(s.nodes.length - 1);
    }
    for (let c = 0; c < faces; c++) {
      const tip = outer[c]!;
      const tipNext = outer[(c + 1) % faces]!;
      addMember(s, rings[lvl]![c]!, tip, armSection, { material: armMat }); // horizontal arm
      addMember(s, rings[lvl - 1]![c]!, tip, armSection, { material: armMat }); // down stay
      addMember(s, rings[lvl + 1]![c]!, tip, armSection, { material: armMat }); // up stay
      addMember(s, tip, tipNext, armSection, { material: armMat }); // tangential tie
    }
  }

  // --- antenna masts: a slim braced vertical crown rising above the tower
  // (Sutro Tower's three-pronged "trident"). A short count-sided X-braced mast
  // grows from the ring at the given height up by `rise`.
  for (const mast of spec.masts ?? []) {
    const n = Math.max(2, mast.count);
    const subs = Math.max(1, mast.panels ?? 4);
    const lvl = Math.min(ringY.length - 1, Math.max(0, levelAtHeight(mast.atHeightFrac * spec.height)));
    const band = bands[Math.min(lvl, panels - 1)]!;
    const mSection = mast.section ? resolveSection(mast.section) : band.leg;
    const mMat = mast.material ? resolveMaterial(mast.material) : band.legMat;
    const yb = ringY[lvl]!;
    const topR = mast.topSpread ?? mast.spread;
    // base ring: reuse the tower ring if the prong count matches faces, else add
    // a base ring and tie each prong foot to its nearest tower node.
    let prev: number[];
    if (n === faces) {
      prev = rings[lvl]!;
    } else {
      prev = [];
      for (const [x, z] of ringPositions(n, mast.spread)) {
        s.nodes.push(makeNode(x, yb, z, false));
        prev.push(s.nodes.length - 1);
      }
      const towerRing = rings[lvl]!;
      for (const p of prev) {
        const np = s.nodes[p]!;
        let best = towerRing[0]!, bd = Infinity;
        for (const ti of towerRing) {
          const nt = s.nodes[ti]!;
          const d = Math.hypot(nt.x - np.x, nt.z - np.z);
          if (d < bd) { bd = d; best = ti; }
        }
        addMember(s, best, p, mSection, { material: mMat });
      }
    }
    for (let k = 1; k <= subs; k++) {
      const y = yb + mast.rise * (k / subs);
      const r = lerp(mast.spread, topR, k / subs);
      const ring: number[] = [];
      for (const [x, z] of ringPositions(n, r)) {
        s.nodes.push(makeNode(x, y, z, false));
        ring.push(s.nodes.length - 1);
      }
      for (let c = 0; c < n; c++) {
        const nx = (c + 1) % n;
        addMember(s, prev[c]!, ring[c]!, mSection, { material: mMat }); // vertical prong
        addMember(s, ring[c]!, ring[nx]!, mSection, { material: mMat }); // belt
        addMember(s, prev[c]!, ring[nx]!, mSection, { material: mMat }); // X diagonal
        addMember(s, prev[nx]!, ring[c]!, mSection, { material: mMat }); // X diagonal
      }
      prev = ring;
    }
  }

  // --- base arches: ornamental arches spanning adjacent legs (Eiffel/Tokyo) ---
  // For each face, a parabolic polyline of members springs from the two legs at
  // `springFrac` and crowns mid-face at `apexFrac`; each arch node is tied up to
  // the nearest ring node at the apex level so the arch is braced, not floppy.
  for (const arch of spec.arches ?? []) {
    const segs = Math.max(2, arch.segments ?? 8);
    const springLvl = Math.min(panels, Math.max(0, levelAtHeight(arch.springFrac * spec.height)));
    const apexLvl = Math.min(panels, Math.max(springLvl + 1, levelAtHeight(arch.apexFrac * spec.height)));
    const band = bands[Math.min(springLvl, panels - 1)]!;
    const aSection = arch.section ? resolveSection(arch.section) : band.brace;
    const aMat = arch.material ? resolveMaterial(arch.material) : band.braceMat;
    const yApex = ringY[apexLvl]!;
    const apexRing = rings[apexLvl]!;
    for (let c = 0; c < faces; c++) {
      const na = s.nodes[rings[springLvl]![c]!]!;
      const nb = s.nodes[rings[springLvl]![(c + 1) % faces]!]!;
      let prevNode = rings[springLvl]![c]!;
      for (let k = 1; k < segs; k++) {
        const t = k / segs;
        const x = lerp(na.x, nb.x, t);
        const z = lerp(na.z, nb.z, t);
        const baseY = lerp(na.y, nb.y, t);
        const y = baseY + (yApex - baseY) * (1 - Math.pow(2 * t - 1, 2)); // parabola 0→1→0
        s.nodes.push(makeNode(x, y, z, false));
        const cur = s.nodes.length - 1;
        addMember(s, prevNode, cur, aSection, { material: aMat }); // arch chain
        // brace ONLY the crown node up to the platform (a single clean hanger per
        // face) — tying every node fans into a mess and hides the arch shape.
        if (k === Math.round(segs / 2)) {
          let best = apexRing[0]!, bd = Infinity;
          for (const ti of apexRing) {
            const nt = s.nodes[ti]!;
            const d = Math.hypot(nt.x - x, nt.z - z);
            if (d < bd) { bd = d; best = ti; }
          }
          addMember(s, cur, best, aSection, { material: aMat });
        }
        prevNode = cur;
      }
      addMember(s, prevNode, rings[springLvl]![(c + 1) % faces]!, aSection, { material: aMat }); // close to leg B
    }
  }

  // --- guys ---
  const anchors: number[] = [];
  let maxAnchorR = Math.max(baseRadius, ...ringR, ...(spec.crossarms ?? []).map((a) => a.radius));
  if (spec.guys) {
    for (const g of spec.guys) {
      const guy = typed(spec.guyTypes, g.guyType, g.section, g.material, "guyStrand");
      const lvl = Math.max(1, levelAtHeight(g.atHeightFrac * spec.height));
      maxAnchorR = Math.max(maxAnchorR, g.anchorRadius);
      const anchorPos = ringPositions(faces, g.anchorRadius);
      for (let c = 0; c < faces; c++) {
        const [ax, az] = anchorPos[c]!;
        const anchor = makeNode(ax, ground(ax, az), az, true);
        s.nodes.push(anchor);
        const ai = s.nodes.length - 1;
        anchors.push(ai);
        addMember(s, rings[lvl]![c]!, ai, guy.section, {
          material: guy.material,
          tensionOnly: true,
          pretensionStrain: g.pretensionStrain,
        });
      }
    }
  }

  computeMasses(s);

  // --- appurtenance loads: attach dead mass + record for per-step wind ---
  if (spec.loads && spec.loads.length > 0) {
    for (const load of spec.loads) {
      const ring = rings[levelAtHeight(load.height)]!;
      const az = load.azimuth ?? 0;
      let node = ring[0]!;
      let best = Infinity;
      for (const idx of ring) {
        const n = s.nodes[idx]!;
        const a = Math.atan2(n.z, n.x);
        const d = Math.abs(Math.atan2(Math.sin(a - az), Math.cos(a - az)));
        if (d < best) {
          best = d;
          node = idx;
        }
      }
      const nd = s.nodes[node]!;
      nd.mass += deadWeight(load);
      nd.invMass = nd.fixed ? 0 : 1 / nd.mass;
      (nd.mountedLoads ??= []).push({ load });
    }
  }

  const render: RenderModel = {
    skin: spec.shell ? "tube" : "lattice",
    rings,
    baseNodes: rings[0]!,
    anchors,
  };
  if (spec.shell) {
    render.tubeRadius = ringY.map(
      (y) =>
        lerp(spec.shell!.baseDiameter, spec.shell!.topDiameter, spec.height > 0 ? y / spec.height : 0) *
        0.5,
    );
  }

  const foundation: ResolvedFoundation = {
    soil: spec.foundation?.soil ?? DEFAULT_SOIL,
    base: spec.foundation?.base ?? DEFAULT_FOUNDATION,
    anchor: spec.foundation?.anchor,
    connection: spec.foundation?.connection,
  };

  // --- attach foundation + connection capacity to fixed base/anchor nodes ---
  // A fixed node with a foundationCap can be RELEASED by the solver when its
  // reaction exceeds capacity (uplift/overturn → Fall Over; anchor → Get Sued).
  // Defaults (competent dense-sand + generous footing/rigid connection) mean
  // built-in towers never lose at the ground, so nothing regresses.
  {
    const soilParams = SOIL_PRESETS[foundation.soil];
    const connCap: ConnectionCapacity = foundation.connection
      ? connectionCapacity(foundation.connection)
      : RIGID_CONNECTION;
    // base-row legs share the base footing (per-leg capacity)
    const baseCap = foundationCapacity(foundation.base, soilParams);
    for (const idx of rings[0]!) {
      s.nodes[idx]!.foundationCap = {
        capacity: baseCap,
        connection: connCap,
        isAnchor: false,
      };
    }
    // guy anchors use the anchor footing (default: a deadman block)
    if (anchors.length > 0) {
      const anchorSpec: FoundationSpec = foundation.anchor ?? DEFAULT_ANCHOR_FOUNDATION;
      const anchorCap = foundationCapacity(anchorSpec, soilParams);
      for (const idx of anchors) {
        s.nodes[idx]!.foundationCap = {
          capacity: anchorCap,
          connection: connCap,
          isAnchor: true,
        };
      }
    }
  }

  return {
    structure: s,
    render,
    type: spec.type,
    height: spec.height,
    halfWidth: maxAnchorR,
    foundation,
  };
}
